import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sdkQueryClient } from '../queryClient';
import type { ApiResponse } from './internalTypes';
import { buildSurveyUrl, mapSurvey, extractSession } from './utils';
import {
  logFetchStart,
  logFetchPerfFailure,
  logFetchPerfSuccess,
  logFetchSuccess,
  logFetchError,
} from './logger/fetchLogger';
import { logMapWarning, logMapSuccess, logMapError } from './logger/mapperLogger';
import { trackFetchLatency, trackFetchError } from './telemetry/fetchTelemetry';
import { getErrorMessage, toSdkLoggableError } from '../types/shared';

/**
 * Performs the network request and returns the raw API response.
 */
async function fetchRawSurveyData(url: string): Promise<ApiResponse> {
  const startTime = performance.now();
  logFetchStart(url);
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'force-cache',
    });

    const durationMs = performance.now() - startTime;

    if (!response.ok) {
      logFetchPerfFailure(durationMs, response.status, url);
      trackFetchError(response.status, url, `Survey fetch failed (${response.status})`);
      throw new Error(`Survey fetch failed (${response.status})`);
    }
    const apiResponse = (await response.json()) as ApiResponse;
    const responseString = JSON.stringify(apiResponse);
    const sizeInBytes = new Blob([responseString]).size;
    const payloadSize = `${(sizeInBytes / 1024).toFixed(2)} KB`;
    logFetchPerfSuccess(durationMs, response.status, url, payloadSize);

    trackFetchLatency(durationMs, response.status, url, payloadSize);

    logFetchSuccess(apiResponse);
    return apiResponse;
  } catch (err: unknown) {
    const durationMs = performance.now() - startTime;
    logFetchPerfFailure(durationMs, 'FAILED', url);
    trackFetchError('FAILED', url, getErrorMessage(err));
    logFetchError(toSdkLoggableError(err));
    throw err;
  }
}

/**
 * A custom React hook that manages fetching survey configurations, mapping the API response,
 * and maintaining state for survey data, session tokens, loading states, and error handling.
 * Uses TanStack Query with a singleton QueryClient to run independently of client providers.
 *
 * @param options Optional settings including active language code and placeholders mapping.
 * @returns Object holding survey, session, loading status, and error references.
 */
export function useFetchSurvey(options?: { instanceId?: string; language?: string; placeholders?: Record<string, string> }) {
  const instanceId = options?.instanceId?.trim() ?? '';
  const url = buildSurveyUrl(instanceId);
  const targetLanguage = options?.language;
  const placeholders = options?.placeholders;

  // The query is keyed purely by url, meaning we fetch the survey layout only once.
  // refetch on window focus, check all refetch policies
  const {
    data: apiResponse,
    error,
    isLoading,
    isSuccess,
  } = useQuery(
    {
      queryKey: ['survey', url],
      queryFn: () => fetchRawSurveyData(url),
      enabled: !!instanceId,
    },
    sdkQueryClient
  );

  // Map the raw data to the active language context dynamically on the client side using useMemo
  const placeholdersKey = placeholders ? JSON.stringify(placeholders) : '';

  const mapped = useMemo(() => {
    // Only map the survey if it has successfully fetched
    if (!isSuccess) {
      return { survey: null, session: null, mapError: null as Error | null };
    }

    if (!apiResponse) {
      logMapWarning();
      return { survey: null, session: null, mapError: null as Error | null };
    }

    try {
      const mappedSurvey = mapSurvey(apiResponse, targetLanguage, placeholders);
      const surveySession = extractSession(apiResponse);
      const updatedSession = surveySession ? { ...surveySession, language: mappedSurvey.language } : null;

      logMapSuccess(mappedSurvey);

      return {
        survey: mappedSurvey,
        session: updatedSession,
        mapError: null as Error | null,
      };
    } catch (err) {
      const mapError = err instanceof Error ? err : new Error(getErrorMessage(err));
      logMapError(toSdkLoggableError(err));
      return { survey: null, session: null, mapError };
    }
  }, [apiResponse, isSuccess, targetLanguage, placeholdersKey, placeholders]);

  return {
    survey: mapped.survey,
    session: mapped.session,
    fetching: isLoading,
    fetchError: error,
    mapError: mapped.mapError,
  };
}
